import Vue from 'vue';
import Vuex from 'vuex';
import { auth, db } from '../firebase';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    user: null,
    loading: false,
    error: null,
    blogs: [],
    blogLoading: false
  },

  getters: {
    isAuthenticated: state => !!state.user,
    currentUser: state => state.user,
    loading: state => state.loading,
    error: state => state.error,
    blogs: state => state.blogs,
    blogLoading: state => state.blogLoading
  },

  mutations: {
    SET_USER(state, user) {
      state.user = user;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_BLOGS(state, blogs) {
      state.blogs = blogs;
    },
    SET_BLOG_LOADING(state, loading) {
      state.blogLoading = loading;
    },
    ADD_BLOG(state, blog) {
      state.blogs.unshift(blog);
    },
    UPDATE_BLOG(state, updatedBlog) {
      const index = state.blogs.findIndex(blog => blog.id === updatedBlog.id);
      if (index !== -1) {
        Vue.set(state.blogs, index, updatedBlog);
      }
    },
    DELETE_BLOG(state, blogId) {
      state.blogs = state.blogs.filter(blog => blog.id !== blogId);
    }
  },

  actions: {
    // Initialize auth state listener
    initAuth({ commit }) {
      return new Promise((resolve) => {
        try {
          auth.onAuthStateChanged((user) => {
            if (user) {
              commit('SET_USER', {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL
              });
            } else {
              commit('SET_USER', null);
            }
            resolve(user);
          }, (error) => {
            console.error('Auth error:', error);
            commit('SET_USER', null);
            resolve(null);
          });
        } catch (error) {
          console.error('Auth initialization error:', error);
          commit('SET_USER', null);
          resolve(null);
        }
      });
    },

    // Login with email and password
    async login({ commit }, { email, password }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const result = await auth.signInWithEmailAndPassword(email, password);
        commit('SET_USER', {
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL
        });
        return result.user;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Register with email and password
    async register({ commit }, { email, password, displayName }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const result = await auth.createUserWithEmailAndPassword(email, password);
        
        // Update profile with display name
        await result.user.updateProfile({
          displayName: displayName
        });

        commit('SET_USER', {
          uid: result.user.uid,
          email: result.user.email,
          displayName: displayName,
          photoURL: result.user.photoURL
        });
        
        return result.user;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Logout
    async logout({ commit }) {
      commit('SET_LOADING', true);
      try {
        await auth.signOut();
        commit('SET_USER', null);
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Fetch all blogs
    async fetchBlogs({ commit }) {
      commit('SET_BLOG_LOADING', true);
      try {
        const snapshot = await db.collection('blogs')
          .orderBy('createdAt', 'desc')
          .get();
        
        const blogs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        commit('SET_BLOGS', blogs);
        return blogs;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_BLOG_LOADING', false);
      }
    },

    // Create a new blog
    async createBlog({ commit, state }, blogData) {
      commit('SET_LOADING', true);
      try {
        const blog = {
          ...blogData,
          authorId: state.user.uid,
          authorName: state.user.displayName || state.user.email,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          published: blogData.published || false,
          views: 0,
          likes: 0
        };

        const docRef = await db.collection('blogs').add(blog);
        const newBlog = { id: docRef.id, ...blog };
        
        commit('ADD_BLOG', newBlog);
        return newBlog;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Update a blog
    async updateBlog({ commit }, { id, data }) {
      commit('SET_LOADING', true);
      try {
        const updateData = {
          ...data,
          updatedAt: new Date().toISOString()
        };

        await db.collection('blogs').doc(id).update(updateData);
        
        const doc = await db.collection('blogs').doc(id).get();
        const updatedBlog = { id: doc.id, ...doc.data() };
        
        commit('UPDATE_BLOG', updatedBlog);
        return updatedBlog;
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Delete a blog
    async deleteBlog({ commit }, blogId) {
      commit('SET_LOADING', true);
      try {
        await db.collection('blogs').doc(blogId).delete();
        commit('DELETE_BLOG', blogId);
      } catch (error) {
        commit('SET_ERROR', error.message);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Clear error
    clearError({ commit }) {
      commit('SET_ERROR', null);
    }
  }
});